import { create } from "zustand";
import { crmApiFetch } from "../services/crmApi";

const useAppDataStore = create((set, get) => ({
	products: [],
	organizations: [],
	organizationOptions: [],
	followUpsByContactId: {},
	pendingContactNav: null,
	productsHydrated: false,
	productsLoading: false,
	productsError: "",
	organizationsLoading: false,
	organizationsError: "",

	hydrateAppData: async (profile) => {
		const userId = profile?.id || null;

		set({
			products: [],
			organizations: [],
			organizationOptions: [],
			followUpsByContactId: {},
			pendingContactNav: null,
			productsHydrated: false,
			productsLoading: false,
			productsError: "",
			organizationsLoading: false,
			organizationsError: "",
		});

		if (!userId) {
			return;
		}

		if (get().productsLoading || get().productsHydrated) {
			return;
		}

		set({
			productsLoading: true,
			productsError: "",
			organizationsLoading: true,
			organizationsError: "",
		});

		try {
			const [productsResponse, organizationsResponse] = await Promise.all([
				crmApiFetch("/api/products", { method: "GET" }),
				crmApiFetch("/api/accounts", { method: "GET" }),
			]);

			let productsPayload = {};
			try {
				productsPayload = await productsResponse.json();
			} catch {
				productsPayload = {};
			}

			if (!productsResponse.ok) {
				throw new Error(productsPayload.error || "Failed to fetch products");
			}

			let organizationsPayload = {};
			try {
				organizationsPayload = await organizationsResponse.json();
			} catch {
				organizationsPayload = {};
			}

			if (!organizationsResponse.ok) {
				throw new Error(organizationsPayload.error || "Failed to fetch organizations");
			}

			const normalizedOrganizationOptions = (Array.isArray(organizationsPayload.accounts)
				? organizationsPayload.accounts
				: []
			)
				.map((org) => ({
					id: org?.id ?? null,
					name: org?.name || "",
				}))
				.filter((org) => Boolean(org.name));

			set({
				products: Array.isArray(productsPayload.products) ? productsPayload.products : [],
				organizations: normalizedOrganizationOptions.map((org) => org.name),
				organizationOptions: normalizedOrganizationOptions,
				productsHydrated: true,
				productsLoading: false,
				productsError: "",
				organizationsLoading: false,
				organizationsError: "",
			});
		} catch (err) {
			set({
				products: [],
				organizations: [],
				organizationOptions: [],
				followUpsByContactId: {},
				productsHydrated: false,
				productsLoading: false,
				productsError: err?.message || "Failed to load products",
				organizationsLoading: false,
				organizationsError: err?.message || "Failed to load organizations",
			});
		}
	},

	setFollowUpsLookup: (followUps = []) =>
		set(() => {
			const grouped = {};
			for (const item of Array.isArray(followUps) ? followUps : []) {
				const contactId = item?.contact_id ?? item?.contactId;
				if (!contactId) {
					continue;
				}
				const key = String(contactId);
				if (!Array.isArray(grouped[key])) {
					grouped[key] = [];
				}
				grouped[key].push(item);
			}

			return {
				followUpsByContactId: grouped,
			};
		}),

	setFollowUpsForContact: (contactId, followUps = []) =>
		set((state) => {
			if (!contactId) {
				return state;
			}
			const key = String(contactId);
			return {
				followUpsByContactId: {
					...state.followUpsByContactId,
					[key]: Array.isArray(followUps) ? followUps : [],
				},
			};
		}),
	
	requestContactNav: (contact) => set({pendingContactNav: contact}),
	clearContactNav: () => set({pendingContactNav: null}),

	addLocalOrganizationOption: (name) =>
		set((state) => {
			const trimmed = (name || "").trim();
			if (!trimmed) {
				return state;
			}

			const exists = state.organizationOptions.some(
				(org) => org.name.toLowerCase() === trimmed.toLowerCase()
			);
			if (exists) {
				return state;
			}

			const nextOrganizationOptions = [...state.organizationOptions, { id: null, name: trimmed }]
				.sort((a, b) => a.name.localeCompare(b.name));

			return {
				organizationOptions: nextOrganizationOptions,
				organizations: nextOrganizationOptions.map((org) => org.name),
			};
		}),

	resetAppData: () =>
		set({
			products: [],
			organizations: [],
			organizationOptions: [],
			followUpsByContactId: {},
			productsHydrated: false,
			productsLoading: false,
			productsError: "",
			organizationsLoading: false,
			organizationsError: "",
		}),
}));

export default useAppDataStore;
